import { forwardRef } from 'react';
import { type IssuedQuizQuote } from '../../utils/quizQuoteApi';
import styles from '../QuoteResultPageView.module.css';
const ASSET_BASE = '/assets/pricing/quote/document';
const PLAN_LABEL_KO = {
  basic: '베이직',
  pro: '프로'
} as const;
const PLAN_LABEL_EN = {
  basic: 'Basic',
  pro: 'Pro'
} as const;
interface QuizQuoteDocumentProps {
  quote: IssuedQuizQuote;
  variant?: 'preview' | 'pdf';
}
function formatWon(value: number): string {
  return value.toLocaleString('ko-KR');
}
function formatKoreanAmount(value: number): string {
  if (value === 0) return '영원';
  const digits = ['', '일', '이', '삼', '사', '오', '육', '칠', '팔', '구'];
  const smallUnits = ['', '십', '백', '천'];
  const largeUnits = ['', '만', '억', '조'];
  const groups = Math.floor(value).toString().split('').reverse();
  const parts: string[] = [];
  for (let groupIndex = 0; groupIndex < groups.length; groupIndex += 4) {
    const groupDigits = groups.slice(groupIndex, groupIndex + 4);
    const groupText = groupDigits.map((digit, digitIndex) => {
      const number = Number(digit);
      if (!number) return '';
      if (number === 1 && digitIndex > 0) return smallUnits[digitIndex];
      return `${digits[number]}${smallUnits[digitIndex]}`;
    }).reverse().join('');
    if (groupText) {
      parts.unshift(`${groupText}${largeUnits[groupIndex / 4]}`);
    }
  }
  return `${parts.join('')}원`;
}
function AmountCell({
  amount,
  compact = false,
  parenthesized = false
}: {
  amount: number;
  compact?: boolean;
  parenthesized?: boolean;
}) {
  const className = [styles.amountInner, compact ? styles.amountCellCompact : ''].filter(Boolean).join(' ');
  return <span className={[styles.amountCell, compact ? styles.amountCellCompact : '', parenthesized ? styles.amountCellParenthesized : ''].filter(Boolean).join(' ')} data-sentry-component="AmountCell" data-sentry-source-file="QuizQuoteDocument.tsx">
      <span className={className}>
        <span className={styles.currency}>₩</span>
        <span className={styles.amountValue}>{formatWon(amount)}</span>
      </span>
    </span>;
}
function formatDocumentDate(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  return `${date.getFullYear()}년 ${date.getMonth() + 1}월 ${date.getDate()}일`;
}
function getPlanFeature(plan: IssuedQuizQuote['product']['plan']): string {
  if (plan === 'basic') {
    return '베이직 전용 맵 이용, AI로 문제 추가하기, 비공개 퀴즈 만들기';
  }
  return '모든 맵 무제한 이용, 고급 AI 기능, 모든 학습리포트 기능, 다음 퀴즈 이어풀기, 워크시트 저장';
}
export function getQuoteDiscountRows(discounts: IssuedQuizQuote['pricing']['discounts']) {
  return discounts.map(({
    label,
    amount
  }) => ({
    label,
    amount
  }));
}
export const QuizQuoteDocument = forwardRef<HTMLDivElement, QuizQuoteDocumentProps>(function QuizQuoteDocument({
  quote,
  variant = 'preview'
}, ref) {
  const annotation = quote.recipient.annotation || '특이사항 없음';
  const discountRows = getQuoteDiscountRows(quote.pricing.discounts);
  const emptyRowCount = Math.max(0, 4 - discountRows.length - (variant === 'pdf' ? 1 : 0));
  return <div ref={ref} className={[styles.est, variant === 'pdf' ? styles.estPdf : ''].filter(Boolean).join(' ')}>
      <div className={styles.documentHeader}>
        ZEP QUIZ {PLAN_LABEL_KO[quote.product.plan]} 플랜 견적서
      </div>
      <div className={`${styles.logo} ${styles.headerLogo}`}>
        <img src={`${ASSET_BASE}/logo.svg`} alt="ZEP Logo" />
      </div>

      <table className={styles.infoTable}>
        <tbody>
          <tr>
            <th>수신</th>
            <td>{quote.recipient.organizationName}</td>
            <th rowSpan={4}>
              공
              <br />
              급
              <br />자
            </th>
            <th>등록번호</th>
            <td colSpan={3}>535-86-02323</td>
          </tr>
          <tr>
            <th>견적명</th>
            <td>
              ZEP QUIZ {PLAN_LABEL_KO[quote.product.plan]} 플랜{' '}
              {quote.product.monthCount}개월 견적
            </td>
            <th>상호명</th>
            <td>주식회사 젭</td>
            <th>대표자</th>
            <td>김상엽, 고창원</td>
          </tr>
          <tr>
            <th>견적날짜</th>
            <td>{formatDocumentDate(quote.issuedAt)}</td>
            <th>주소</th>
            <td colSpan={3}>서울시 강남구 선릉로 93길 50, 11층</td>
          </tr>
          <tr>
            <th>유효기간</th>
            <td>{formatDocumentDate(quote.validUntil)}까지</td>
            <th>전화번호</th>
            <td>070-7666-9837</td>
            <th>이메일</th>
            <td>biz@zep.us</td>
          </tr>
        </tbody>
      </table>

      <table className={styles.infoTable}>
        <tbody>
          <tr>
            <th>견적가</th>
            <td className={styles.strong}>
              {`금 ${formatKoreanAmount(quote.pricing.finalAmount)} 정(₩${formatWon(quote.pricing.finalAmount)}), VAT 포함`}
            </td>
          </tr>
        </tbody>
      </table>

      <table className={styles.priceTable} style={{
      tableLayout: 'fixed'
    }}>
        <colgroup>
          <col style={{
          width: '8%'
        }} />
          <col style={{
          width: '8%'
        }} />
          <col style={{
          width: '14%'
        }} />
          <col style={{
          width: '21%'
        }} />
          <col style={{
          width: '17%'
        }} />
          <col style={{
          width: '32%'
        }} />
        </colgroup>
        <thead>
          <tr>
            <th colSpan={6}>산출 내역</th>
          </tr>
        </thead>
        <tbody>
          <tr className={styles.strong}>
            <td>No.</td>
            <td>플랜</td>
            <td>사용 인원</td>
            <td>1인 당 단가</td>
            <td>이용 개월 수</td>
            <td>결제 금액</td>
          </tr>
          <tr>
            <td>1</td>
            <td>
              <strong>{PLAN_LABEL_EN[quote.product.plan]}</strong>
            </td>
            <td>{quote.product.userCount}</td>
            <td>
              <AmountCell amount={quote.pricing.regularUnitAmount} compact />
            </td>
            <td>{quote.product.monthCount}</td>
            <td>
              <AmountCell amount={quote.pricing.originalAmount} />
            </td>
          </tr>
          {discountRows.map((discount, index) => <tr key={`${discount.label}-${index}`}>
              <td>{index + 2}</td>
              <td colSpan={4}>{discount.label}</td>
              <td>
                <AmountCell amount={discount.amount} parenthesized />
              </td>
            </tr>)}
          {Array.from({
          length: emptyRowCount
        }).map((_, index) => <tr key={index}>
              <td> &nbsp;</td>
              <td />
              <td />
              <td />
              <td />
              <td />
            </tr>)}
          <tr>
            <td colSpan={5} className={styles.finalPrice}>
              최종 견적
            </td>
            <td className={styles.finalPrice}>
              <AmountCell amount={quote.pricing.finalAmount} />
            </td>
          </tr>
        </tbody>
      </table>

      <table className={`${styles.detailsTable} ${styles.featureTable}`} style={{
      tableLayout: 'fixed'
    }}>
        <colgroup>
          <col style={{
          width: '7%'
        }} />
          <col style={{
          width: '15%'
        }} />
          <col style={{
          width: '78%'
        }} />
        </colgroup>
        <thead>
          <tr>
            <th colSpan={3}>기능 상세</th>
          </tr>
          <tr>
            <th>번 호</th>
            <th>항 목</th>
            <th>내 용</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>1</td>
            <td>사용 인원</td>
            <td>{quote.product.userCount}명</td>
          </tr>
          <tr>
            <td>2</td>
            <td>사용 시작일</td>
            <td>{formatDocumentDate(quote.product.startDate)}</td>
          </tr>
          <tr>
            <td>3</td>
            <td>만료일</td>
            <td>{formatDocumentDate(quote.product.endDate)}</td>
          </tr>
          <tr>
            <td>4</td>
            <td>{PLAN_LABEL_KO[quote.product.plan]} 플랜 기능</td>
            <td>{getPlanFeature(quote.product.plan)}</td>
          </tr>
          <tr>
            <td>5</td>
            <td>요금제 유형</td>
            <td>
              {quote.product.pricingType === 'group' ? '학교·단체' : '개인'}
            </td>
          </tr>
        </tbody>
      </table>

      <table className={styles.detailsTable}>
        <thead>
          <tr>
            <th colSpan={2}>비고</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td className={styles.annotation}>{annotation}</td>
            <td className={styles.sign}>
              &nbsp;
              <br />
              &nbsp;
              <img src={`${ASSET_BASE}/zep_stamp.png`} alt="" />
              <br />
              &nbsp;
            </td>
          </tr>
        </tbody>
      </table>
    </div>;
});